import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock, MapPin, Shield, AlertCircle } from 'lucide-react';
import { CHAIN_NAME } from '@/utils/chain';

interface AccessHistoryProps {
  petId: string;
  onClose: () => void;
}

interface AccessLog {
  id: string;
  timestamp: string;
  accessor: string;
  accessorType: 'vet' | 'groomer' | 'emergency' | 'owner';
  action: string;
  dataAccessed: string[];
  location?: string;
  ipAddress: string;
  sessionDuration: number;
  status: 'completed' | 'active' | 'denied';
}

export function AccessHistory({ petId: _petId, onClose }: AccessHistoryProps) {
  const { t } = useTranslation();
  const [filterType, setFilterType] = useState('all');

  const accessLogs: AccessLog[] = [];

  const filteredLogs =
    filterType === 'all'
      ? accessLogs
      : accessLogs.filter((log) => log.accessorType === filterType);

  const getAccessorIcon = (type: string) => {
    switch (type) {
      case 'vet':
        return '🏥';
      case 'groomer':
        return '✂️';
      case 'emergency':
        return '🚨';
      case 'owner':
        return '👤';
      default:
        return '❓';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span
            className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
          >
            {t('features.qrHub.accessHistorySection.completed', 'Completed')}
          </span>
        );
      case 'active':
        return (
          <span
            className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
          >
            {t('features.qrHub.accessHistorySection.active', 'Active')}
          </span>
        );
      case 'denied':
        return (
          <span
            className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
          >
            {t('features.qrHub.accessHistorySection.denied', 'Denied')}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8" />
              <div>
                <h2
                  className="text-2xl mb-1"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.accessHistorySection.title', 'Access History')}
                </h2>
                <p
                  className="text-sm opacity-90"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.qrHub.accessHistorySection.subtitle', 'Complete audit trail of all pet data access events')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'vet', 'groomer', 'emergency', 'owner'].map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filterType === type
                    ? 'bg-white text-indigo-600'
                    : 'bg-white/20 hover:bg-white/30'
                }`}
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {type === 'all'
                  ? t('features.qrHub.accessHistorySection.allAccess', 'All Access')
                  : t(`features.qrHub.accessHistorySection.${type}`, type.charAt(0).toUpperCase() + type.slice(1))}
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="p-6 bg-gray-50 dark:bg-[#0F0E16] border-b border-gray-200 dark:border-white/8">
          <div className="grid md:grid-cols-5 gap-4">
            <div className="text-center">
              <div
                className="text-2xl text-indigo-600 dark:text-indigo-400 mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {accessLogs.length}
              </div>
              <div
                className="text-sm text-gray-600 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.accessHistorySection.totalAccess', 'Total Access')}
              </div>
            </div>
            <div className="text-center">
              <div
                className="text-2xl text-green-600 dark:text-green-400 mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {accessLogs.filter((l) => l.accessorType === 'vet').length}
              </div>
              <div
                className="text-sm text-gray-600 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.accessHistorySection.vetAccess', 'Vet Access')}
              </div>
            </div>
            <div className="text-center">
              <div
                className="text-2xl text-purple-600 dark:text-purple-400 mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {accessLogs.filter((l) => l.accessorType === 'groomer').length}
              </div>
              <div
                className="text-sm text-gray-600 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.accessHistorySection.groomerAccess', 'Groomer Access')}
              </div>
            </div>
            <div className="text-center">
              <div
                className="text-2xl text-red-600 dark:text-red-400 mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {
                  accessLogs.filter((l) => l.accessorType === 'emergency')
                    .length
                }
              </div>
              <div
                className="text-sm text-gray-600 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.accessHistorySection.emergencyAccess', 'Emergency Access')}
              </div>
            </div>
            <div className="text-center">
              <div
                className="text-2xl text-gray-600 dark:text-gray-400 mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {accessLogs.filter((l) => l.status === 'denied').length}
              </div>
              <div
                className="text-sm text-gray-600 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.accessHistorySection.denied', 'Denied')}
              </div>
            </div>
          </div>
        </div>

        {/* Access Logs */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-4">
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                className={`bg-white dark:bg-[#0F0E16] border rounded-lg p-6 ${
                  log.status === 'denied'
                    ? 'border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-900/20'
                    : 'border-gray-200 dark:border-white/8'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="text-3xl">
                    {getAccessorIcon(log.accessorType)}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4
                            className="text-gray-900 dark:text-white"
                            style={{
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {log.accessor}
                          </h4>
                          {getStatusBadge(log.status)}
                        </div>
                        <div
                          className="text-sm text-gray-600 dark:text-white/50"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {log.action}
                        </div>
                      </div>
                      <div
                        className="text-right text-sm text-gray-500 dark:text-white/50"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <div className="flex items-center gap-1 justify-end mb-1">
                          <Clock className="w-3 h-3" />
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                        {log.sessionDuration > 0 && (
                          <div>{t('features.qrHub.accessHistorySection.sessionMinutes', { count: log.sessionDuration, defaultValue: '{{count}} min session' })}</div>
                        )}
                      </div>
                    </div>

                    {log.dataAccessed.length > 0 && (
                      <div className="mb-3">
                        <div
                          className="text-sm text-gray-600 dark:text-white/50 mb-2"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {t('features.qrHub.accessHistorySection.dataAccessed', 'Data Accessed:')}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {log.dataAccessed.map((data, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-sm"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              {data}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div
                      className="flex items-center gap-6 text-sm text-gray-500 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {log.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span>{log.location}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        <span className="font-mono text-xs">
                          {log.ipAddress}
                        </span>
                      </div>
                    </div>

                    {log.status === 'denied' && (
                      <div
                        className="mt-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-400"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <AlertCircle className="w-4 h-4" />
                        <span>
                          {t('features.qrHub.accessHistorySection.accessDeniedReason', 'Access was denied - QR code expired or revoked')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Blockchain Transaction */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/8">
                  <div className="flex items-center justify-between text-xs">
                    <div
                      className="text-gray-500 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.qrHub.accessHistorySection.blockchainTransaction', 'Blockchain Transaction')}
                    </div>
                    <div className="font-mono text-purple-600 dark:text-purple-400">
                      TX: 0x{Math.random().toString(16).substr(2, 16)}...
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredLogs.length === 0 && (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-gray-300 dark:text-white/20 mx-auto mb-4" />
              <p
                className="text-gray-500 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.accessHistorySection.noLogsFound', 'No access logs found')}
              </p>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="border-t border-gray-200 dark:border-white/8 p-6 bg-gray-50 dark:bg-[#0F0E16]">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5" />
            <div
              className="text-sm text-gray-700 dark:text-white/70"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <strong className="text-gray-900 dark:text-white">
                {t('features.qrHub.accessHistorySection.immutableAuditTrail', 'Immutable Audit Trail')}
              </strong>{' '}
              {t('features.qrHub.accessHistorySection.immutableDesc', `All access events are permanently recorded on the ${CHAIN_NAME} blockchain, ensuring a tamper-proof history of who accessed your pet's data and when.`, { chain: CHAIN_NAME })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
